import { useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import {
  MessageCircle,
  Send,
  Globe,
  Instagram,
  MessagesSquare,
  Bot,
  BotOff,
  Search,
  Settings,
  RefreshCw,
} from 'lucide-react';
import Modal from '../components/Modal';

const CHANNELS = ['WHATSAPP', 'INSTAGRAM', 'MESSENGER', 'TELEGRAM', 'WEBSITE'] as const;
type Channel = (typeof CHANNELS)[number];

const channelMeta: Record<string, { label: string; Icon: any; color: string }> = {
  WHATSAPP: { label: 'WhatsApp', Icon: MessageCircle, color: 'text-green-600' },
  INSTAGRAM: { label: 'Instagram', Icon: Instagram, color: 'text-pink-600' },
  MESSENGER: { label: 'Messenger', Icon: MessagesSquare, color: 'text-blue-600' },
  TELEGRAM: { label: 'Telegram', Icon: Send, color: 'text-sky-500' },
  WEBSITE: { label: 'Site', Icon: Globe, color: 'text-gray-600' },
};

interface Conversation {
  id: string;
  channel: Channel;
  contactName?: string;
  contactHandle?: string;
  status: string;
  aiEnabled: boolean;
  unreadCount: number;
  lastMessagePreview?: string;
  lastMessageAt?: string;
}

interface ChatMessage {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  body: string;
  aiGenerated: boolean;
  status: string;
  createdAt: string;
}

export default function InboxPage() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selected, setSelected] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [reply, setReply] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showChannels, setShowChannels] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  async function loadConversations() {
    setLoading(true);
    try {
      const params: any = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get('/messaging/conversations', { params });
      setConversations(data.items || []);
    } finally {
      setLoading(false);
    }
  }

  async function openConversation(conv: Conversation) {
    setSelected(conv);
    const { data } = await api.get(`/messaging/conversations/${conv.id}`);
    setMessages(data.messages || []);
    if (conv.unreadCount > 0) {
      await api.post(`/messaging/conversations/${conv.id}/read`).catch(() => {});
      setConversations((prev) =>
        prev.map((c) => (c.id === conv.id ? { ...c, unreadCount: 0 } : c)),
      );
    }
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }

  async function sendReply() {
    if (!selected || !reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/messaging/conversations/${selected.id}/reply`, { text: reply });
      setReply('');
      const { data } = await api.get(`/messaging/conversations/${selected.id}`);
      setMessages(data.messages || []);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Falha ao enviar mensagem');
    } finally {
      setSending(false);
    }
  }

  async function toggleAi() {
    if (!selected) return;
    const next = !selected.aiEnabled;
    await api.patch(`/messaging/conversations/${selected.id}`, { aiEnabled: next });
    setSelected({ ...selected, aiEnabled: next });
  }

  useEffect(() => {
    loadConversations();
    const t = setInterval(loadConversations, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)]">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold dark:text-dark-text-primary">Atendimento (Inbox)</h1>
        <button
          onClick={() => setShowChannels(true)}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:text-dark-text-primary"
        >
          <Settings className="w-4 h-4" /> Configurar canais
        </button>
      </div>

      <div className="flex flex-1 gap-4 min-h-0">
        {/* Lista de conversas */}
        <div className="w-80 flex flex-col bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
          <div className="p-3 border-b border-gray-200 dark:border-dark-border space-y-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadConversations()}
                placeholder="Buscar contato..."
                className="w-full pl-8 pr-2 py-2 text-sm border border-gray-200 dark:border-dark-border rounded-lg dark:bg-dark-bg-tertiary dark:text-dark-text-primary"
              />
            </div>
            <div className="flex gap-1 text-xs">
              {['', 'OPEN', 'PENDING', 'CLOSED'].map((s) => (
                <button
                  key={s || 'ALL'}
                  onClick={() => setStatusFilter(s)}
                  className={`px-2 py-1 rounded ${statusFilter === s ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-dark-bg-tertiary dark:text-dark-text-secondary'}`}
                >
                  {s === '' ? 'Todas' : s === 'OPEN' ? 'Abertas' : s === 'PENDING' ? 'Pendentes' : 'Fechadas'}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && conversations.length === 0 && (
              <p className="p-4 text-sm text-gray-400 flex items-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" /> Carregando...
              </p>
            )}
            {!loading && conversations.length === 0 && (
              <p className="p-4 text-sm text-gray-400">Nenhuma conversa ainda.</p>
            )}
            {conversations.map((c) => {
              const meta = channelMeta[c.channel];
              const Icon = meta?.Icon || MessageCircle;
              return (
                <button
                  key={c.id}
                  onClick={() => openConversation(c)}
                  className={`w-full text-left px-3 py-3 border-b border-gray-100 dark:border-dark-border flex gap-3 hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary ${selected?.id === c.id ? 'bg-primary-50 dark:bg-dark-bg-tertiary' : ''}`}
                >
                  <Icon className={`w-5 h-5 mt-1 ${meta?.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm truncate dark:text-dark-text-primary">
                        {c.contactName || c.contactHandle || 'Contato'}
                      </span>
                      {c.unreadCount > 0 && (
                        <span className="ml-2 bg-primary-600 text-white text-xs rounded-full px-1.5">
                          {c.unreadCount}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-dark-text-secondary truncate">
                      {c.lastMessagePreview || '—'}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Conversa selecionada */}
        <div className="flex-1 flex flex-col bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              Selecione uma conversa
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-border flex items-center justify-between">
                <div>
                  <p className="font-medium dark:text-dark-text-primary">
                    {selected.contactName || selected.contactHandle}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-dark-text-secondary">
                    {channelMeta[selected.channel]?.label} · {selected.status}
                  </p>
                </div>
                <button
                  onClick={toggleAi}
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border ${selected.aiEnabled ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}
                  title="Liga/desliga a IA nesta conversa"
                >
                  {selected.aiEnabled ? <Bot className="w-4 h-4" /> : <BotOff className="w-4 h-4" />}
                  IA {selected.aiEnabled ? 'ligada' : 'desligada'}
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-dark-bg-primary">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`flex ${m.direction === 'OUTBOUND' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] px-3 py-2 rounded-lg text-sm ${
                        m.direction === 'OUTBOUND'
                          ? 'bg-primary-600 text-white'
                          : 'bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border dark:text-dark-text-primary'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{m.body}</p>
                      <div className="text-[10px] opacity-70 mt-1 flex gap-1 justify-end">
                        {m.aiGenerated && <Bot className="w-3 h-3" />}
                        {new Date(m.createdAt).toLocaleString('pt-BR')}
                        {m.status === 'FAILED' && <span className="text-red-300">· falhou</span>}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              <div className="p-3 border-t border-gray-200 dark:border-dark-border flex gap-2">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendReply();
                    }
                  }}
                  rows={2}
                  placeholder="Escreva uma resposta..."
                  className="flex-1 px-3 py-2 text-sm border border-gray-200 dark:border-dark-border rounded-lg resize-none dark:bg-dark-bg-tertiary dark:text-dark-text-primary"
                />
                <button
                  onClick={sendReply}
                  disabled={sending || !reply.trim()}
                  className="px-4 bg-primary-600 text-white rounded-lg disabled:opacity-50 flex items-center"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {showChannels && <ChannelsModal onClose={() => setShowChannels(false)} />}
    </div>
  );
}

// Campos de credenciais esperados por canal (rótulos prontos para preencher).
const CHANNEL_FIELDS: Record<Channel, { key: string; label: string; help?: string }[]> = {
  TELEGRAM: [
    { key: 'botToken', label: 'Bot Token', help: 'Gerado no @BotFather' },
    { key: 'secretToken', label: 'Secret Token (opcional)', help: 'String aleatória para validar o webhook' },
  ],
  WHATSAPP: [
    { key: 'accessToken', label: 'Access Token' },
    { key: 'phoneNumberId', label: 'Phone Number ID' },
    { key: 'verifyToken', label: 'Verify Token' },
    { key: 'appSecret', label: 'App Secret' },
  ],
  INSTAGRAM: [
    { key: 'pageAccessToken', label: 'Page Access Token' },
    { key: 'verifyToken', label: 'Verify Token' },
    { key: 'appSecret', label: 'App Secret' },
  ],
  MESSENGER: [
    { key: 'pageAccessToken', label: 'Page Access Token' },
    { key: 'verifyToken', label: 'Verify Token' },
    { key: 'appSecret', label: 'App Secret' },
  ],
  WEBSITE: [
    { key: 'allowedOrigins', label: 'Domínios do site (separados por vírgula)', help: 'Ex.: https://www.suaimobiliaria.com.br' },
  ],
};

function ChannelsModal({ onClose }: { onClose: () => void }) {
  const [integrations, setIntegrations] = useState<any[]>([]);
  const [channel, setChannel] = useState<Channel>('TELEGRAM');
  const [enabled, setEnabled] = useState(true);
  const [aiAutoReply, setAiAutoReply] = useState(true);
  const [displayName, setDisplayName] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // IA (Claude)
  const [aiKey, setAiKey] = useState('');
  const [aiModel, setAiModel] = useState('claude-opus-4-7');
  const [aiConfigured, setAiConfigured] = useState(false);
  const [savingAi, setSavingAi] = useState(false);

  async function load() {
    const [ints, ai] = await Promise.all([
      api.get('/messaging/integrations'),
      api.get('/messaging/ai-settings').catch(() => ({ data: {} })),
    ]);
    setIntegrations(ints.data || []);
    if (ai.data?.model) setAiModel(ai.data.model);
    setAiConfigured(!!ai.data?.configured);
  }
  useEffect(() => {
    load();
  }, []);

  // limpa os campos ao trocar de canal
  useEffect(() => {
    setFields({});
  }, [channel]);

  function buildConfig() {
    const config: Record<string, any> = {};
    for (const f of CHANNEL_FIELDS[channel]) {
      const v = fields[f.key]?.trim();
      if (!v) continue;
      config[f.key] = f.key === 'allowedOrigins' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v;
    }
    return config;
  }

  async function save() {
    setSaving(true);
    try {
      await api.put('/messaging/integrations', {
        channel,
        enabled,
        aiAutoReply,
        displayName: displayName || undefined,
        config: buildConfig(),
      });
      await load();
      alert('Canal salvo.');
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Falha ao salvar canal.');
    } finally {
      setSaving(false);
    }
  }

  async function saveAi() {
    setSavingAi(true);
    try {
      await api.put('/messaging/ai-settings', {
        apiKey: aiKey || undefined,
        model: aiModel,
      });
      setAiKey('');
      await load();
      alert('Configuração da IA salva no banco.');
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Falha ao salvar IA.');
    } finally {
      setSavingAi(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="Configurar canais e IA" size="2xl">
      <div className="space-y-5">
        {/* IA */}
        <div className="border border-gray-200 dark:border-dark-border rounded-lg p-3">
          <p className="text-sm font-semibold mb-2 dark:text-dark-text-primary">
            IA (Claude) — qualificação e auto-resposta
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            <label className="text-sm dark:text-dark-text-primary">
              Chave da API Anthropic {aiConfigured && <span className="text-green-600">(já salva ✓)</span>}
              <input
                type="password"
                value={aiKey}
                onChange={(e) => setAiKey(e.target.value)}
                placeholder={aiConfigured ? '•••••••• (deixe vazio p/ manter)' : 'sk-ant-...'}
                className="mt-1 w-full border border-gray-200 dark:border-dark-border rounded-lg px-2 py-2 dark:bg-dark-bg-tertiary dark:text-dark-text-primary"
              />
            </label>
            <label className="text-sm dark:text-dark-text-primary">
              Modelo
              <select
                value={aiModel}
                onChange={(e) => setAiModel(e.target.value)}
                className="mt-1 w-full border border-gray-200 dark:border-dark-border rounded-lg px-2 py-2 dark:bg-dark-bg-tertiary dark:text-dark-text-primary"
              >
                <option value="claude-opus-4-7">claude-opus-4-7 (mais capaz)</option>
                <option value="claude-haiku-4-5">claude-haiku-4-5 (mais barato/rápido)</option>
                <option value="claude-sonnet-4-6">claude-sonnet-4-6 (equilibrado)</option>
              </select>
            </label>
          </div>
          <button
            onClick={saveAi}
            disabled={savingAi}
            className="mt-2 px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50 text-sm"
          >
            {savingAi ? 'Salvando...' : 'Salvar IA'}
          </button>
        </div>

        {/* Canal */}
        <div className="border border-gray-200 dark:border-dark-border rounded-lg p-3 space-y-3">
          <p className="text-sm font-semibold dark:text-dark-text-primary">Canal de atendimento</p>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm dark:text-dark-text-primary">
              Canal
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value as Channel)}
                className="mt-1 w-full border border-gray-200 dark:border-dark-border rounded-lg px-2 py-2 dark:bg-dark-bg-tertiary dark:text-dark-text-primary"
              >
                {CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {channelMeta[c].label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm dark:text-dark-text-primary">
              Nome de exibição
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="mt-1 w-full border border-gray-200 dark:border-dark-border rounded-lg px-2 py-2 dark:bg-dark-bg-tertiary dark:text-dark-text-primary"
              />
            </label>
          </div>

          {CHANNEL_FIELDS[channel].map((f) => (
            <label key={f.key} className="text-sm block dark:text-dark-text-primary">
              {f.label}
              <input
                value={fields[f.key] || ''}
                onChange={(e) => setFields((p) => ({ ...p, [f.key]: e.target.value }))}
                className="mt-1 w-full border border-gray-200 dark:border-dark-border rounded-lg px-2 py-2 dark:bg-dark-bg-tertiary dark:text-dark-text-primary"
              />
              {f.help && <span className="text-xs text-gray-400">{f.help}</span>}
            </label>
          ))}

          <div className="flex gap-4 text-sm dark:text-dark-text-primary">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              Ativo
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={aiAutoReply}
                onChange={(e) => setAiAutoReply(e.target.checked)}
              />
              Resposta automática por IA
            </label>
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50 text-sm"
          >
            {saving ? 'Salvando...' : 'Salvar canal'}
          </button>
        </div>

        {/* Lista */}
        <div>
          <p className="text-sm font-medium mb-2 dark:text-dark-text-primary">Canais configurados</p>
          {integrations.length === 0 && <p className="text-sm text-gray-400">Nenhum.</p>}
          {integrations.map((i) => (
            <div key={i.id} className="flex justify-between text-sm py-1 dark:text-dark-text-secondary">
              <span>{channelMeta[i.channel]?.label || i.channel}</span>
              <span>
                {i.enabled ? '🟢 ativo' : '⚪ inativo'} {i.aiAutoReply ? '· IA auto' : ''}{' '}
                {i.configured ? '· credenciais ✓' : '· sem credenciais'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}
